import {
  categorize,
  type EvmSignData,
  type EvmSignMessageOptions,
  HYPERLIQUID_SPOT_MARKET_ORDER_TYPED_DATA_NAME,
  hypercore,
  type IEvmSigner,
  submitToHyperliquid,
  waitForReceiptAndValidate,
} from '@polaris/core/evm'
import { featureFlagsAtom } from '@polaris/core/stores'
import { atomsGlobalStore, debug, LockedWalletError } from '@polaris/core/utils'
import {
  estimateFeesPerGas,
  estimateGas,
  getAccount,
  switchChain,
} from '@wagmi/core'
import { useCallback, useMemo } from 'react'
import {
  createPublicClient,
  createWalletClient,
  custom,
  type FeeValuesEIP1559,
  type SendTransactionParameters,
  type SendTransactionRequest,
  type TypedDataDefinition,
  UserRejectedRequestError,
} from 'viem'
import { addChain } from 'viem/actions'
import { type Config, useAccount } from 'wagmi'
import {
  getConnectorClientQueryOptions,
  signMessageMutationOptions,
  signTypedDataMutationOptions,
} from 'wagmi/query'
import { getQueryClient } from '@/src/configs/tanstack-query'
import { wagmiConfig } from '@/src/ecosystems/evm/config'
import { wrapCatchCategorize } from '@/src/utils/error'

// Helper to send a tx with minimal resiliency:
// - Some wallets/providers occasionally return no tx hash on first call.
//   We treat that as a transient and retry once after a short delay.
// - This does NOT change any tx fields. It just mitigates flaky hash returns
//   without re-prompting the user.
async function trySendWithOneRetry<TParams>(
  walletClient: { sendTransaction: (params: TParams) => Promise<string> },
  params: TParams,
): Promise<string | undefined> {
  for (let i = 0; i < 2; i++) {
    const result = await walletClient.sendTransaction(params)
    if (result) return result
    await new Promise((resolve) => setTimeout(resolve, 1000))
    debug(
      'useEvmWalletSigner.signAndBroadcast: did not receive tx hash when sending evm transaction, retrying...',
    )
  }
  return undefined
}

// Helper to wait for connector to be ready before operations
async function waitForConnectorReady(maxWaitMs = 5000): Promise<void> {
  const startTime = Date.now()

  let { status } = getAccount(wagmiConfig)

  // Early return if already connected
  if (status === 'connected') return

  // Poll until status is 'connected' or timeout
  while (status === 'connecting' || status === 'reconnecting') {
    if (Date.now() - startTime > maxWaitMs) {
      throw new LockedWalletError(
        'Wallet is still reconnecting. Please try again.',
      )
    }

    // Wait 100ms before checking again
    await new Promise((resolve) => setTimeout(resolve, 100))
    status = getAccount(wagmiConfig).status
  }

  // After loop, if disconnected throw error
  if (status === 'disconnected') {
    throw new LockedWalletError('Wallet disconnected')
  }
}

export const useEvmWalletSigner = () => {
  const { address, connector } = useAccount()

  const deriveAddress = useCallback(() => {
    return address
  }, [address])

  const estimateFees = useCallback(
    async (
      options: EvmSignData,
    ): Promise<{
      gasLimit: bigint
      feeData: FeeValuesEIP1559
    }> => {
      await waitForConnectorReady()

      const chainId = options.chain.id.toString()
      const isHyperliquid =
        chainId.split(':').pop() === 'hyperliquid' ||
        options.chain.id === hypercore.id

      // Hyperliquid is gasless
      if (isHyperliquid) {
        return {
          gasLimit: BigInt(0),
          feeData: {
            maxFeePerGas: BigInt(0),
            maxPriorityFeePerGas: BigInt(0),
          },
        }
      }

      if (atomsGlobalStore.get(featureFlagsAtom).forceBroadcast) {
        // Return dummy values when force broadcast is enabled
        return {
          gasLimit: BigInt(
            atomsGlobalStore.get(featureFlagsAtom).forceBroadcastGasLimit,
          ),
          feeData: {
            maxFeePerGas: BigInt(0),
            maxPriorityFeePerGas: BigInt(0),
          },
        }
      }

      const chainIdNum = options.chain.id as 1 | 42161 | 8453 | 10 | 137

      try {
        const [feeData, gasLimit] = await Promise.all([
          estimateFeesPerGas(wagmiConfig, {
            chainId: chainIdNum,
          }),
          estimateGas(wagmiConfig, {
            ...(options.transaction as SendTransactionRequest),
            chainId: chainIdNum,
            account: address,
          }),
        ])

        return {
          feeData,
          gasLimit,
        }
      } catch (error) {
        throw new Error('Failed to estimate fees and gas limit', {
          cause: error,
        })
      }
    },
    [address],
  )

  const signAndBroadcast = useCallback(
    async (options: EvmSignData) => {
      // Wait for connector to be fully ready
      await waitForConnectorReady()

      const chainId = options.chain.id

      // Try to switch to current chain, if it fails it could mean that chain is missing,
      // so we add it and then switch to it.
      try {
        await switchChain<Config, number>(wagmiConfig, {
          chainId,
        })
      } catch (e) {
        const connectorClient = await getQueryClient().ensureQueryData(
          getConnectorClientQueryOptions<Config, number>(wagmiConfig, {
            chainId,
          }),
        )

        if (!(e instanceof UserRejectedRequestError)) {
          await addChain(connectorClient, {
            chain: options.chain,
          })
        }

        await switchChain<Config, number>(wagmiConfig, {
          chainId,
        })
      }

      // Check if this is a typed data transaction
      if ('typedData' in options.transaction) {
        let data = (options.transaction as { typedData: TypedDataDefinition })
          .typedData

        const isHyperliquidSpotMarketOrder =
          options.transaction.typedDataName ===
          HYPERLIQUID_SPOT_MARKET_ORDER_TYPED_DATA_NAME

        // The spot market order does not contain "hyperliquid" in the domain. As a result, we
        // check its typed data name propagated from the memo.
        const isHyperliquid =
          data.domain?.name?.toLowerCase().includes('hyperliquid') ||
          isHyperliquidSpotMarketOrder

        const { mutationFn: signTypedDataFn } =
          signTypedDataMutationOptions(wagmiConfig)

        let message = data.message

        const action = options.transaction.action
        const typedDataName = options.transaction.typedDataName

        if (!action) {
          throw new Error(
            `action is required for ${options.transaction.typedDataName}`,
          )
        }

        // The nonce is propagated in different fields for different typed data types.
        // We check all options to find the nonce.
        const nonce = (message.nonce ??
          message.time ??
          options.transaction.nonce) as number

        // The spot market order is a special case because it is not a "user signed action" as defined
        // by Hyperliquid. To handle this message, we generate a nonce in the backend, contructing a connection
        // ID from the nonce for signing here.
        // See app/route-providers/hyperliquid/hyperliquid.go for more details.
        if (
          'typedDataName' in options.transaction &&
          isHyperliquidSpotMarketOrder
        ) {
          const nonce = options.transaction.nonce

          // Validate the nonce and action are present
          if (!nonce) {
            throw new Error(`nonce is required for ${typedDataName}`)
          }

          // Convert base64 connectionId to hex
          const messageWithConnectionId = data.message as Record<
            string,
            unknown
          >
          const connectionIdBytes = base64ToHex(
            messageWithConnectionId.connectionId as string,
          )

          // Update typedData to use hex connectionId
          // This is because the connectionId is a base64 string (auto-serialized) and we need to convert it to a hex string
          // for the typed data.
          data = {
            ...data,
            message: {
              ...data.message,
              connectionId: connectionIdBytes,
            },
          }

          message = action
        }

        // Sign the typed data
        const signature = await signTypedDataFn({
          ...data,
          account: address as `0x${string}`,
          domain: {
            ...data.domain,
            chainId: data.domain?.chainId
              ? Number(data.domain.chainId)
              : undefined,
          },
        })

        // If the chain is Hyperliquid, submit the transaction to Hyperliquid
        if (isHyperliquid) {
          await submitToHyperliquid(action, nonce, signature)
        }

        // For non-Hyperliquid chains, return the signature
        return signature
      }
      // Attempt to use estimated gas limit if provided.
      // Note: undefined signals to estimate gas via wallet.
      const gas = options.estimatedGasLimit ?? undefined

      if (gas === undefined) {
        debug('gas is undefined, expecting to simulate via wallet')
      }

      // Get the fee data from the options. By default, we let the wallet/provider
      // determine the appropriate fees at signing time to avoid stale base fee issues.
      // Only honor provided fees when force broadcast is enabled.
      const { forceBroadcast } = atomsGlobalStore.get(featureFlagsAtom)
      if (!options.fees || !forceBroadcast) {
        debug('fees will be determined by wallet/provider at signing time')
      }
      const feeData = forceBroadcast ? options.fees?.feeData : undefined

      // Get the connector client to access the wallet
      const connectorClient = await getQueryClient().ensureQueryData(
        getConnectorClientQueryOptions<Config, number>(wagmiConfig, {
          chainId,
        }),
      )

      // Create a wallet client with the proper transport
      const walletClient = createWalletClient({
        account: address as `0x${string}`,
        chain: options.chain,
        transport: custom(connectorClient.transport),
      })

      // Create a public client to fetch the nonce
      const publicClient = createPublicClient({
        chain: options.chain,
        transport: custom(connectorClient.transport),
      })

      // Fetch the nonce from the network
      // Note: despite wagmi/viem's ability to fetch the nonce on its own,
      // by doing this on our side, we have greater control and improved observability.
      // Previously, we would see nonce issues be abstracted by the wallet without any opportunity to debug.
      let nonce: number | undefined
      try {
        nonce = await publicClient.getTransactionCount({
          address: address as `0x${string}`,
          // We use pending here because we want to get the next nonce, which is the nonce of the next transaction.
          // It returns the transaction count including all pending transactions in the mempool that haven't been mined yet.
          // This gives you the next available nonce.
          // If you have multiple transactions waiting in the mempool, using 'latest' would give you a nonce
          // that's already been used by a pending transaction, causing a nonce conflict.
          blockTag: 'pending',
        })
      } catch (error) {
        debug(
          `useEvmWalletSigner.signAndBroadcast: failed to get nonce for address ${address}, using transport ${JSON.stringify(connectorClient.transport)}`,
          error,
        )
      }

      const sendTransactionRequest = {
        ...options.transaction,
        account: address,
        gas: gas,
        // Note: this implies that we only support legacy or EIP-1559 fee types.
        // When forceBroadcast is disabled, we omit fee fields so the wallet/provider
        // can choose fresh values and avoid base fee race conditions.

        // Legacy transactions use gasPrice
        gasPrice: feeData?.gasPrice,

        // EIP-1559 transactions use maxFeePerGas and maxPriorityFeePerGas
        maxFeePerGas: feeData?.maxFeePerGas,
        maxPriorityFeePerGas: feeData?.maxPriorityFeePerGas,

        // EIP-4844 transactions use maxFeePerBlobGas and other EIP-1559 fields
        // Most wallets do not use this field but we include it for completeness.
        maxFeePerBlobGas: feeData?.maxFeePerBlobGas,

        nonce: nonce,
      }

      // Remove account and chain from the request as it's already set in the wallet client
      const {
        account: _,
        chain: __,
        ...txParams
      } = sendTransactionRequest as SendTransactionParameters<
        typeof options.chain,
        { address: `0x${string}`; type: 'json-rpc' },
        undefined,
        SendTransactionRequest<typeof options.chain, undefined>
      >

      let blockNumber: bigint | undefined
      try {
        // Get the block number to to append it for observability
        blockNumber = await publicClient.getBlockNumber()
      } catch (error) {
        debug(
          `useEvmWalletSigner.signAndBroadcast: failed to get blockNumber for address ${address}, using transport ${JSON.stringify(connectorClient.transport)}`,
          error,
        )
      }

      // Get wallet info from the connector
      const walletId = connector?.id
      debug(
        `sendTransactionRequest with wallet ${walletId}, block number ${blockNumber}`,
        txParams,
      )

      let txHash: string | undefined
      const hasExplicitFees = Boolean(
        txParams.gasPrice ||
          txParams.maxFeePerGas ||
          txParams.maxPriorityFeePerGas ||
          txParams.maxFeePerBlobGas,
      )

      // First attempt: send with current params (may include explicit fee caps when forceBroadcast).
      // trySend may internally retry once if no hash is returned due to wallet flakiness.
      // Send the transaction. If explicit EIP-1559 fee caps are present and the
      // base fee rises between estimation and submission, some nodes/wallets will
      // reject with a base-fee mismatch error. We catch that below and retry once
      // without explicit fee fields so the wallet/provider can pick fresh fees
      // without requiring the user to re-sign.
      try {
        txHash = await trySendWithOneRetry(walletClient, txParams)
      } catch (e) {
        // We hit this branch when the RPC/provider rejects because the current
        // EIP-1559 base fee exceeded our provided maxFeePerGas (race condition).
        const msg = (e as Error)?.message || String(e)
        const isBaseFeeError =
          msg.includes('max fee per gas less than block base fee') ||
          (msg.includes('fee cap') &&
            msg.includes('cannot be lower than the block base fee'))

        // Parse the error message to extract fee information for better debugging
        if (isBaseFeeError) {
          const parsedError = parseGasFeeError(msg)
          debug(
            'useEvmWalletSigner.signAndBroadcast: base fee error detected during sendTransaction',
            {
              originalError: msg,
              parsedError,
              hasExplicitFees,
              txParams: {
                maxFeePerGas: txParams.maxFeePerGas?.toString(),
                maxPriorityFeePerGas: txParams.maxPriorityFeePerGas?.toString(),
                gasPrice: txParams.gasPrice?.toString(),
                gas: txParams.gas?.toString(),
                nonce: txParams.nonce,
              },
            },
          )

          // For base fee errors, always try to retry with fresh fee estimation
          // This handles cases where even wallet-determined fees are stale
          if (parsedError.parsed && parsedError.baseFee) {
            debug(
              'useEvmWalletSigner.signAndBroadcast: attempting retry with adjusted fees based on current baseFee',
            )

            try {
              // Get fresh fee estimation with a buffer above the current base fee
              // EIP-1559 allows max 12.5% base fee change per block, so 15% buffer is sufficient
              const currentBaseFee = BigInt(parsedError.baseFee)
              const bufferMultiplier = BigInt(115) // 15% buffer
              const adjustedMaxFeePerGas =
                (currentBaseFee * bufferMultiplier) / BigInt(100)

              // Use a reasonable priority fee relative to the adjusted max fee
              // Priority fee should be much smaller than maxFeePerGas, typically 1-10% of base fee
              let priorityFee = currentBaseFee / BigInt(20) // 5% of base fee
              // If we have explicit fees, use them. Otherwise, use the default.
              if (hasExplicitFees && txParams.maxPriorityFeePerGas) {
                priorityFee = txParams.maxPriorityFeePerGas
              }

              // Create adjusted params for EIP-1559 transaction
              const adjustedTxParams = {
                to: txParams.to,
                data: txParams.data,
                value: txParams.value,
                gas: txParams.gas,
                nonce: txParams.nonce,
                type: 'eip1559' as const,
                maxFeePerGas: adjustedMaxFeePerGas,
                maxPriorityFeePerGas: priorityFee,
              }

              debug(
                'useEvmWalletSigner.signAndBroadcast: retrying with adjusted fees',
                {
                  originalBaseFee: parsedError.baseFee,
                  adjustedMaxFeePerGas: adjustedMaxFeePerGas.toString(),
                  adjustedMaxPriorityFeePerGas: priorityFee.toString(),
                  maxFeePerGasGwei: (
                    Number(adjustedMaxFeePerGas) / 1e9
                  ).toFixed(4),
                  priorityFeeGwei: (Number(priorityFee) / 1e9).toFixed(4),
                },
              )

              txHash = await trySendWithOneRetry(walletClient, adjustedTxParams)
            } catch (retryError) {
              debug(
                'useEvmWalletSigner.signAndBroadcast: retry with adjusted fees failed, falling back to no explicit fees',
                retryError,
              )

              // Final fallback: remove all fee fields and let wallet handle it completely
              const {
                gasPrice: _g,
                maxFeePerGas: _m,
                maxPriorityFeePerGas: _p,
                maxFeePerBlobGas: _b,
                ...withoutFees
              } = txParams

              txHash = await trySendWithOneRetry(
                walletClient,
                withoutFees as typeof txParams,
              )
            }
          } else if (hasExplicitFees) {
            // Legacy behavior: if we had explicit fees, try without them
            debug(
              'useEvmWalletSigner.signAndBroadcast: base fee > maxFeePerGas, retrying without explicit fee fields',
            )

            const {
              gasPrice: _g,
              maxFeePerGas: _m,
              maxPriorityFeePerGas: _p,
              maxFeePerBlobGas: _b,
              ...withoutFees
            } = txParams

            txHash = await trySendWithOneRetry(
              walletClient,
              withoutFees as typeof txParams,
            )
          } else {
            // No explicit fees and couldn't parse error - just rethrow
            throw e
          }
        } else {
          throw e
        }
      }

      // Catch missing tx hash issue early.
      // If it is not returned, it could mean that the transaction was not sent so the swap
      // would fail anyways.
      if (!txHash) {
        throw new Error('did not receive tx hash when sending evm transaction')
      }

      // Wait for transaction receipt with retry logic and validate it succeeded.
      // We return the hash from the receipt rather than the original txHash because
      // the transaction may have been replaced (e.g., user sped up via wallet).
      // When replaced, viem returns the replacement tx's receipt with the new hash.
      const receipt = await waitForReceiptAndValidate(
        publicClient,
        txHash as `0x${string}`,
        'useEvmWalletSigner.signAndBroadcast',
      )

      return receipt.transactionHash
    },
    [address, connector?.id],
  )

  const signMessage = useCallback(async (options: EvmSignMessageOptions) => {
    const { mutationFn: signMessage } = signMessageMutationOptions(wagmiConfig)

    const signature = await signMessage(options.args)

    return signature
  }, [])

  return useMemo(
    () => ({
      deriveAddress: wrapCatchCategorize(deriveAddress, categorize),
      estimateFees: wrapCatchCategorize(estimateFees, categorize),
      signAndBroadcast: wrapCatchCategorize(signAndBroadcast, categorize),
      signMessage: wrapCatchCategorize(signMessage, categorize),
    }),
    [deriveAddress, estimateFees, signAndBroadcast, signMessage],
  ) satisfies IEvmSigner
}

// Helper function to parse gas fee error messages
function parseGasFeeError(errorMessage: string) {
  const result: {
    address?: string
    maxFeePerGas?: string
    baseFee?: string
    maxPriorityFeePerGas?: string
    parsed: boolean
  } = { parsed: false }

  try {
    // Parse error message like: "max fee per gas less than block base fee: address 0xaEC0baE8714477dF5bE12062C6dE580894470dbF, maxFeePerGas: 13667000 baseFee: 14095000"
    const addressMatch = errorMessage.match(/address\s+(0x[a-fA-F0-9]{40})/)
    const maxFeeMatch = errorMessage.match(/maxFeePerGas:\s*(\d+)/)
    const baseFeeMatch = errorMessage.match(/baseFee:\s*(\d+)/)
    const maxPriorityFeeMatch = errorMessage.match(
      /maxPriorityFeePerGas:\s*(\d+)/,
    )

    if (addressMatch) result.address = addressMatch[1]
    if (maxFeeMatch) result.maxFeePerGas = maxFeeMatch[1]
    if (baseFeeMatch) result.baseFee = baseFeeMatch[1]
    if (maxPriorityFeeMatch)
      result.maxPriorityFeePerGas = maxPriorityFeeMatch[1]

    // Consider parsed if we have at least address and baseFee
    result.parsed = !!(addressMatch && baseFeeMatch)
  } catch (error) {
    debug('parseGasFeeError: failed to parse error message', error)
  }

  return result
}

// Add this helper function
function base64ToHex(base64String: string) {
  // Decode base64 to bytes
  const bytes = Buffer.from(base64String, 'base64')

  // Ensure it's exactly 32 bytes for bytes32
  if (bytes.length !== 32) {
    throw new Error(`Expected 32 bytes, got ${bytes.length} bytes`)
  }

  // Convert to hex with 0x prefix
  return `0x${bytes.toString('hex')}`
}
